import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import Icon from '../../common/Icon';
import RestMark from '../../RestMark';
import {
  canResizeSlice,
  canSplitSlice,
  holdSlice,
  placeDivider,
  removeSlice,
  resizeSlice,
  restSlice,
  sliceStarts,
  splitSlice,
} from '../../../utils/barSlices';
import { INVERSION_ORDINALS, splitInversion } from '../../../utils/chordNotes';
import { formatNumber } from '../../../utils/format';

import { ReactComponent as SplitIcon } from '../../../icons/split.svg';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import { ReactComponent as VoicingIcon } from '../../../icons/music-double-note.svg';
import { ReactComponent as SortIcon } from '../../../icons/sort.svg';
import { ReactComponent as DragIcon } from '../../../icons/drag-dots.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as RestIcon } from '../../../icons/rest-quarter.svg';
import { ReactComponent as TapIcon } from '../../../icons/drum.svg';
import { ReactComponent as CopyIcon } from '../../../icons/copy.svg';
import { ReactComponent as PasteIcon } from '../../../icons/paste-clipboard.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';

import './BarEditor.scss';

const formatBeats = (beats) => formatNumber(Math.round(beats * 100) / 100);

const beatsLabel = (t, beats) => t('dialogs.barEditor.beats', { count: beats, beats: formatBeats(beats) });

const ChordName = ({ chord }) => {
  const { symbol, inversion } = splitInversion(chord);

  return (
    <>
      {symbol}
      {inversion > 0 && <sup>{INVERSION_ORDINALS[inversion]}</sup>}
    </>
  );
};

ChordName.propTypes = { chord: PropTypes.string.isRequired };

// Beats nobody has written yet, named as short as the slice needs
const missingLabel = (t, beats, beatsPerBar) => (beats / beatsPerBar < 0.25 ? '?' : t('dialogs.barEditor.pickChord'));

/**
 * One bar opened up: its chords laid across the beats they hold, with the line between two of them
 * dragged to move beats from one to the other. Nothing here reaches past the bar, so it always adds
 * up to the time signature.
 *
 * The palette writes into the selected slice while this is open — see ChordBarsModal's handleChord.
 */
const BarEditor = ({
  slices,
  selected,
  barNumber,
  beatsPerBar,
  step,
  heldChord,
  familyOf,
  melody,
  onToggleMelody,
  onSelect,
  onChange,
  onChangeChord,
  onVoicing,
  voicingLabel,
  onReorder,
  onMove,
  isCopied = false,
  onCopy,
  onPaste,
  canPaste = false,
  onDone,
  countIn,
  tapping,
}) => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  // A divider being dragged, and the bar as it would be if it were let go here
  const [drag, setDrag] = useState(null);
  // A tapped rhythm shows in the track before it is applied, and the bar can't be edited under it
  const isTapping = tapping.phase !== 'idle';
  const suggestion = tapping.phase === 'done' ? tapping.suggestion : null;
  // Tapping over the count-in, the beat of it to come in on — marked through the first bar only
  const entryBeat =
    tapping.phase === 'recording' && tapping.countInBar && tapping.beat < beatsPerBar ? (countIn?.beat ?? 0) : 0;
  const shown = suggestion?.slices ?? drag?.slices ?? slices;
  const [isKeyPressed, setKeyPressed] = useState(false);

  useEffect(() => {
    if (!tapping.isRunning) return undefined;

    const isTapKey = (event) => event.key === ' ' || event.key === 'Shift';
    const press = (event) => isTapKey(event) && setKeyPressed(true);
    const release = (event) => isTapKey(event) && setKeyPressed(false);
    const releaseAll = () => setKeyPressed(false);

    document.addEventListener('keydown', press);
    document.addEventListener('keyup', release);
    window.addEventListener('blur', releaseAll);

    return () => {
      document.removeEventListener('keydown', press);
      document.removeEventListener('keyup', release);
      window.removeEventListener('blur', releaseAll);
      setKeyPressed(false);
    };
  }, [tapping.isRunning]);

  const starts = sliceStarts(shown);
  const slice = shown[selected] ?? shown[0];
  const isTie = slice.chord === null && !slice.missing && !slice.rest;
  const isHold = shown.length === 1 && isTie;
  // Under a tapped result, the gap the next picked chord fills stands in for the selection
  const isNextGap = (number, index) => suggestion?.gap?.number === number && suggestion.gap.index === index;

  // What a tie is holding: the last chord before it, or nothing when a rest came in between
  const nameOf = (index) => {
    const before = shown
      .slice(0, index)
      .reverse()
      .find(({ chord, rest }) => chord !== null || rest);

    if (before) return before.rest ? '' : before.chord;

    return heldChord;
  };

  const beatAt = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();

    return ((clientX - rect.left) / rect.width) * beatsPerBar;
  };

  const startDrag = (divider) => (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ divider, slices });
  };

  const moveDrag = (event) => {
    if (!drag) return;

    const next = placeDivider(slices, drag.divider, beatAt(event.clientX), step);

    if (next !== drag.slices) setDrag({ ...drag, slices: next });
  };

  // One drag is one edit, so it is one undo
  const endDrag = () => {
    if (drag && drag.slices !== slices) onChange(drag.slices, selected);

    setDrag(null);
  };

  const nudgeDivider = (divider) => (event) => {
    const delta = { ArrowLeft: -step, ArrowRight: step }[event.key];

    if (!delta) return;

    event.preventDefault();
    onChange(placeDivider(slices, divider, starts[divider + 1] + delta, step), selected);
  };

  const resize = (delta) => onChange(resizeSlice(slices, selected, delta, step), selected);

  const change = ({ slices: next, selected: at }) => onChange(next, at);

  return (
    <div className="BarEditor">
      <div className="bar-header">
        <span className="bar-title">
          {t('dialogs.barEditor.bar', { number: barNumber })}{' '}
          <span className="count">· {beatsLabel(t, beatsPerBar)}</span>
        </span>
        {/* A held bar plays nothing of its own, so there is nothing for it to play as notes */}
        <button
          type="button"
          className={cx('melody-toggle', { on: melody })}
          aria-pressed={melody}
          disabled={isHold}
          onClick={onToggleMelody}
        >
          ♪ {t('player.melody')}
        </button>
        <button type="button" className="done" onClick={onDone}>
          {t('common.done')}
        </button>
      </div>

      <div className="track" ref={trackRef}>
        {Array.from({ length: Math.round(beatsPerBar / step) - 1 }, (_, i) => (i + 1) * step).map((at) => (
          <span
            key={at}
            className={cx('tick', { 'tick--beat': Number.isInteger(at) })}
            style={{ left: `${(at / beatsPerBar) * 100}%` }}
            aria-hidden="true"
          />
        ))}

        {shown.map(({ chord, beats, missing, rest, pick }, index) => {
          const tie = chord === null && !missing && !rest;
          const name = tie ? nameOf(index) : chord;
          // Under a tapped result, only a slot a chord was or can be put into is pressed, to change it
          const isSlot = Boolean(suggestion) && pick !== undefined;

          return (
            <button
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              type="button"
              className={cx('slice', chord !== null && familyOf(chord) && `chord-family--${familyOf(chord)}`, {
                'slice--tie': tie,
                'slice--rest': rest,
                'slice--missing': missing,
                'slice--selected': isTapping ? isNextGap(barNumber, index) : index === selected,
              })}
              style={{ flexBasis: `${(beats / beatsPerBar) * 100}%` }}
              aria-pressed={isTapping ? isNextGap(barNumber, index) : index === selected}
              disabled={isTapping && !isSlot}
              onClick={() => (isTapping ? tapping.onPickSlot(pick) : onSelect(index))}
            >
              <span className="slice-name">
                {missing && missingLabel(t, beats, beatsPerBar)}
                {tie && (name ? <ChordName chord={name} /> : t('player.rest'))}
                {/* A first bar come in during the count-in opens on the count, drawn as its numbers */}
                {rest && (
                  <RestMark countIn={countIn?.beat > 0 && index === 0 && shown.length > 1 ? beats : 0} />
                )}
                {chord !== null && <ChordName chord={chord} />}
              </span>
              <span className="slice-beats">
                {tie && name ? t('dialogs.barEditor.heldBeats', { beats: formatBeats(beats) }) : formatBeats(beats)}
              </span>
            </button>
          );
        })}

        {!isTapping &&
          shown.slice(1).map((_, i) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              role="slider"
              tabIndex={0}
              className={cx('divider', { 'divider--dragging': drag?.divider === i })}
              style={{ left: `${(starts[i + 1] / beatsPerBar) * 100}%` }}
              aria-label={t('dialogs.barEditor.dividerLabel', { from: i + 1, to: i + 2 })}
              aria-valuemin={0}
              aria-valuemax={beatsPerBar}
              aria-valuenow={starts[i + 1]}
              onPointerDown={startDrag(i)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={() => setDrag(null)}
              onKeyDown={nudgeDivider(i)}
            >
              <span className="grip" />
            </span>
          ))}
      </div>

      {tapping.isRunning && (
        <div className="tap-panel">
          <div className="tap-count" aria-live="polite">
            {/* The count-in counted out loud, so a first click lost to waking headphones can't shift it */}
            <span className="tap-label">
              {tapping.beat < 0 && t('dialogs.barEditor.getReady')}
              {tapping.phase === 'counting' &&
                tapping.beat >= 0 &&
                t('dialogs.barEditor.countInBeat', { beat: tapping.beat + 1 })}
              {/* Over the count-in, the count is the first bar rather than one before it */}
              {tapping.phase === 'recording' &&
                t('dialogs.barEditor.bar', {
                  number: barNumber + Math.floor(tapping.beat / beatsPerBar) - (tapping.countInBar ? 0 : 1),
                })}
              {entryBeat > 0 && ` · ${t('dialogs.barEditor.inOn', { beat: entryBeat })}`}
            </span>
            <span className="tap-beats" aria-hidden="true">
              {Array.from({ length: beatsPerBar }, (_, i) => (
                <span
                  key={i}
                  className={cx('tap-beat', { on: tapping.beat % beatsPerBar === i, entry: entryBeat === i + 1 })}
                />
              ))}
            </span>
          </div>
          {/* On pointerdown: a click only lands when the finger lifts, which is late */}
          <button
            type="button"
            className={cx('tap-btn', { 'tap-btn--pressed': isKeyPressed })}
            onPointerDown={(event) => tapping.onTap(event.timeStamp)}
          >
            {t('dialogs.barEditor.tap')}
            <span className="tap-keys">{t('dialogs.barEditor.tapKeys')}</span>
          </button>
          <button type="button" className="tap-stop" onClick={tapping.onStop}>
            {t('dialogs.barEditor.stop')}
          </button>
        </div>
      )}

      {tapping.phase === 'done' && (
        <div className="tap-result">
          {!suggestion && <span className="tap-summary">{t('dialogs.barEditor.noTaps')}</span>}
          {suggestion && !suggestion.slices && (
            <span className="tap-summary">{t('dialogs.barEditor.barStays', { number: barNumber })}</span>
          )}

          {/* Every later bar the rhythm reaches, drawn the way this one is so the notes can be followed
              over the bar line */}
          {suggestion?.following.map(({ number, slices: later }) => (
            <div key={number} className="tap-bar">
              <span className="tap-bar-number">{t('dialogs.barEditor.bar', { number })}</span>
              <div className="track track--preview">
                {later.map(({ chord, beats, missing, pick }, index) => (
                  <button
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    type="button"
                    className={cx('slice', chord !== null && familyOf(chord) && `chord-family--${familyOf(chord)}`, {
                      'slice--tie': chord === null && !missing,
                      'slice--missing': missing,
                      'slice--selected': isNextGap(number, index),
                    })}
                    style={{ flexBasis: `${(beats / beatsPerBar) * 100}%` }}
                    aria-pressed={isNextGap(number, index)}
                    disabled={pick === undefined}
                    onClick={() => tapping.onPickSlot(pick)}
                  >
                    <span className="slice-name">
                      {missing && missingLabel(t, beats, beatsPerBar)}
                      {chord === null && !missing && t('dialogs.barEditor.holdSlice')}
                      {chord !== null && <ChordName chord={chord} />}
                    </span>
                    <span className="slice-beats">{formatBeats(beats)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {suggestion && <span className="tap-hint">{t('dialogs.barEditor.tapHint')}</span>}

          {/* The first tap is beat 1; a rhythm that comes in later is moved there by hand */}
          {suggestion && (
            <div className="tap-shift">
              <button
                type="button"
                className="step-btn"
                aria-label={t('dialogs.barEditor.startEarlier')}
                disabled={tapping.shift <= 0}
                onClick={() => tapping.onShift(-1)}
              >
                <span className="chevron chevron--left" aria-hidden="true" />
              </button>
              <span className="readout">
                {t('dialogs.barEditor.startsOnBeat', { beat: formatBeats(1 + tapping.shift) })}
              </span>
              <button
                type="button"
                className="step-btn"
                aria-label={t('dialogs.barEditor.startLater')}
                disabled={tapping.shift >= beatsPerBar - step}
                onClick={() => tapping.onShift(1)}
              >
                <span className="chevron chevron--right" aria-hidden="true" />
              </button>
            </div>
          )}

          <div className="tap-actions">
            <button type="button" className="tap-again" onClick={tapping.onStart}>
              {t('dialogs.barEditor.tryAgain')}
            </button>
            <button type="button" className="tap-cancel" onClick={tapping.onCancel}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {!isTapping && (
        <div className="length">
          <button
            type="button"
            className="step-btn"
            aria-label={t('dialogs.barEditor.shorter')}
            disabled={!canResizeSlice(slices, selected, -step, step)}
            onClick={() => resize(-step)}
          >
            −
          </button>
          <span className="readout">{beatsLabel(t, slice.beats)}</span>
          <button
            type="button"
            className="step-btn"
            aria-label={t('dialogs.barEditor.longer')}
            disabled={!canResizeSlice(slices, selected, step, step)}
            onClick={() => resize(step)}
          >
            +
          </button>
          <span className="hint">
            {shown.length === 1 ? t('dialogs.barEditor.splitHint') : t('dialogs.barEditor.pickHint')}
          </span>
        </div>
      )}

      {/* Only on the chart's first bar: where in the count-in it comes in, or after it as usual */}
      {!isTapping && countIn && (
        <div className="entry-picker" role="group" aria-label={t('dialogs.barEditor.entryGroup')}>
          <span className="entry-label">{t('dialogs.barEditor.comesIn')}</span>
          <button
            type="button"
            className={cx('entry-btn', { on: countIn.beat === 0 })}
            aria-pressed={countIn.beat === 0}
            onClick={() => countIn.onChoose(0)}
          >
            {t('dialogs.barEditor.afterCountIn')}
          </button>
          {Array.from({ length: beatsPerBar }, (_, i) => (
            <button
              key={i}
              type="button"
              className={cx('entry-btn', 'entry-btn--beat', { on: countIn.beat === i + 1 })}
              aria-pressed={countIn.beat === i + 1}
              aria-label={t('dialogs.barEditor.onCountInBeat', { beat: i + 1 })}
              onClick={() => countIn.onChoose(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {!isTapping && (
        <div className="tools">
          <button
            type="button"
            className="action"
            disabled={!canSplitSlice(slices, selected, step)}
            onClick={() => change(splitSlice(slices, selected, step))}
          >
            <Icon icon={<SplitIcon />} />
            {t('dialogs.barEditor.split')}
          </button>
          {/* The first slice becomes the chord from the bar before still ringing; a later one is
            taken back by the chord before it. Nothing to do on a slice that is already held, or in
            a bar with only the one — holding that would empty the bar. */}
          <button
            type="button"
            className="action"
            disabled={isTie || shown.length < 2}
            onClick={() => change(holdSlice(slices, selected))}
          >
            <span className="hold-mark" aria-hidden="true" />
            {t('dialogs.barEditor.hold')}
          </button>
          {/* Silence until the next chord, where Hold keeps the one before ringing */}
          <button
            type="button"
            className="action"
            disabled={Boolean(slice.rest)}
            onClick={() => change(restSlice(slices, selected))}
          >
            <Icon icon={<RestIcon />} />
            {t('player.rest')}
          </button>
          {slice.chord !== null && (
            <button type="button" className="action" onClick={onChangeChord}>
              <Icon icon={<EditIcon />} />
              {t('dialogs.barEditor.typeChord')}
            </button>
          )}
          {onVoicing && (
            <button type="button" className="action" onClick={onVoicing}>
              <Icon icon={<VoicingIcon />} />
              {t('dialogs.barEditor.voicing', { label: voicingLabel })}
            </button>
          )}
          {onReorder && (
            <button type="button" className="action" onClick={onReorder}>
              <Icon icon={<SortIcon />} />
              {t('dialogs.barEditor.reorder')}
            </button>
          )}
          {onMove && (
            <button type="button" className="action" onClick={onMove}>
              <Icon icon={<DragIcon />} />
              {t('dialogs.barEditor.moveToGap')}
            </button>
          )}
          <button
            type="button"
            className="action action--remove"
            disabled={isHold}
            onClick={() => change(removeSlice(slices, selected))}
          >
            <Icon icon={<TrashIcon />} />
            {t('dialogs.barEditor.remove')}
          </button>
          {/* A tap is measured against the tempo, so a part without one can't be tapped */}
          <button type="button" className="action" disabled={!tapping.canTap} onClick={tapping.onStart}>
            <Icon icon={<TapIcon />} />
            {tapping.canTap ? t('dialogs.barEditor.tapRhythm') : t('dialogs.barEditor.tapRhythmNoBpm')}
          </button>
          <button type="button" className="action" onClick={onCopy}>
            <Icon icon={isCopied ? <CheckIcon /> : <CopyIcon />} />
            {isCopied ? t('dialogs.chordBars.copied') : t('dialogs.barEditor.copyBar')}
          </button>
          {/* Only once a bar has been copied, and greyed over the bar it was copied from */}
          {onPaste && (
            <button type="button" className="action" disabled={!canPaste} onClick={onPaste}>
              <Icon icon={<PasteIcon />} />
              {t('dialogs.barEditor.pasteBar')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const sliceShape = PropTypes.shape({
  chord: PropTypes.string,
  beats: PropTypes.number.isRequired,
  missing: PropTypes.bool,
  // Under a tapped result, the slot's index among the chords put in by hand
  pick: PropTypes.number,
});

BarEditor.propTypes = {
  slices: PropTypes.arrayOf(sliceShape).isRequired,
  selected: PropTypes.number.isRequired,
  barNumber: PropTypes.number.isRequired,
  beatsPerBar: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  heldChord: PropTypes.string,
  familyOf: PropTypes.func.isRequired,
  melody: PropTypes.bool,
  onToggleMelody: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeChord: PropTypes.func.isRequired,
  onVoicing: PropTypes.func,
  voicingLabel: PropTypes.string,
  onReorder: PropTypes.func,
  onMove: PropTypes.func,
  isCopied: PropTypes.bool,
  onCopy: PropTypes.func.isRequired,
  // Absent until a bar that fits this meter has been copied
  onPaste: PropTypes.func,
  canPaste: PropTypes.bool,
  onDone: PropTypes.func.isRequired,
  // The chart's first bar only: the beat of the count-in it comes in on (0 for after it), and the
  // choice of another
  countIn: PropTypes.shape({ beat: PropTypes.number.isRequired, onChoose: PropTypes.func.isRequired }),
  tapping: PropTypes.shape({
    phase: PropTypes.oneOf(['idle', 'counting', 'recording', 'done']).isRequired,
    isRunning: PropTypes.bool.isRequired,
    beat: PropTypes.number.isRequired,
    // The count-in is the first bar tapped over rather than one ahead of it
    countInBar: PropTypes.bool,
    canTap: PropTypes.bool.isRequired,
    // Null when nothing was heard. `slices` is this bar, null when it is left as it is; `following`
    // is every later bar the rhythm reaches.
    suggestion: PropTypes.shape({
      slices: PropTypes.arrayOf(sliceShape),
      following: PropTypes.arrayOf(
        PropTypes.shape({ number: PropTypes.number.isRequired, slices: PropTypes.arrayOf(sliceShape).isRequired }),
      ).isRequired,
      // Where the next picked chord goes: a bar by its number, and a slice in it. Null once full and
      // none is tapped.
      gap: PropTypes.shape({ number: PropTypes.number.isRequired, index: PropTypes.number.isRequired }),
    }),
    onStart: PropTypes.func.isRequired,
    onTap: PropTypes.func.isRequired,
    onStop: PropTypes.func.isRequired,
    // Beats into the bar the first tapped note lands on
    shift: PropTypes.number.isRequired,
    onShift: PropTypes.func.isRequired,
    // A slot of the result tapped, by its pick index, to put the next chord there
    onPickSlot: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
  }).isRequired,
};

export default BarEditor;
